import momentz from 'moment-timezone';

export const DASHBOARD_PIE_EVENTS_SCHEMA_VERSION = 1;
export const DASHBOARD_PIE_EVENTS_KIND = 'dashboard.pie.events';
export const DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS = 60;
export const DASHBOARD_PIE_EVENTS_DEFAULT_LIMIT = 10;
export const DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE = 'pie';
export const DASHBOARD_PIE_EVENTS_SUPPORTED_CHART_TYPES = ['pie', 'doughnut', 'kpi', 'line', 'alerts_by_device_heatmap', 'operator_action_time'];
export const DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE = {
	color: '#1E88E5',
	icon: 'mdi-shield-check',
};

const DEFAULT_TIMEZONE = 'America/Lima';
const DEFAULT_TIME_PRESET = 'last_24h';

export const DASHBOARD_PIE_EVENTS_ROLLING_PRESETS = {
	last_24h: { amount: 24, unit: 'hours' },
	last_7d: { amount: 7, unit: 'days' },
	last_30d: { amount: 30, unit: 'days' },
	week: { amount: 7, unit: 'days' },
	'15d': { amount: 15, unit: 'days' },
	'30d': { amount: 30, unit: 'days' },
};

export const DASHBOARD_CHART_INTERVAL_OPTIONS = [
	{ value: 'today', labelKey: 'dashboard_pie_events_preset_today' },
	{ value: 'yesterday', labelKey: 'dashboard_pie_events_preset_yesterday' },
	{ value: 'week', labelKey: 'dashboard_pie_events_preset_week' },
	{ value: '15d', labelKey: 'dashboard_pie_events_preset_15d' },
	{ value: '30d', labelKey: 'dashboard_pie_events_preset_30d' },
];

export function normalizeDashboardChartIntervalPreset(preset){
	if (preset === 'last_7d') return 'week';
	if (preset === 'last_30d') return '30d';
	if (preset === 'last_24h') return 'today';
	return preset || 'today';
}

export function buildDashboardChartTimeRange(preset, timezone){
	const normalizedPreset = normalizeDashboardChartIntervalPreset(preset);
	const tz = resolveTimezone(timezone);

	if (normalizedPreset === 'today' || normalizedPreset === 'yesterday') {
		return {
			preset: normalizedPreset,
			custom: { from: null, to: null, tz },
		};
	}

	return buildRollingTimeRange(normalizedPreset, tz);
}

function resolveTimezone(value){
	return value && momentz.tz.zone(value) ? value : DEFAULT_TIMEZONE;
}

function normalizeTimeRange(time, defaults){
	const custom = time.custom || {};
	const preset = time.preset || defaults.time.preset;
	const tz = resolveTimezone(custom.tz || time.tz || defaults.time.custom.tz);
	const rollingPreset = DASHBOARD_PIE_EVENTS_ROLLING_PRESETS[preset];

	if (preset === 'today' && !custom.from && !custom.to) {
		const now = momentz.tz(tz);
		return {
			preset,
			custom: {
				from: now.clone().startOf('day').toISOString(),
				to: now.toISOString(),
				tz,
			},
		};
	}

	if (preset === 'yesterday' && !custom.from && !custom.to) {
		const now = momentz.tz(tz);
		const yesterday = now.clone().subtract(1, 'day');
		return {
			preset,
			custom: {
				from: yesterday.clone().startOf('day').toISOString(),
				to: yesterday.clone().endOf('day').toISOString(),
				tz,
			},
		};
	}

	if (rollingPreset && !custom.from && !custom.to) {
		return buildRollingTimeRange(preset, tz);
	}

	return {
		preset,
		custom: {
			from: custom.from || null,
			to: custom.to || null,
			tz,
		},
	};
}

export function buildRollingTimeRange(preset, timezone){
	const tz = resolveTimezone(timezone);
	const rollingPreset = DASHBOARD_PIE_EVENTS_ROLLING_PRESETS[preset] || DASHBOARD_PIE_EVENTS_ROLLING_PRESETS[DEFAULT_TIME_PRESET];
	const now = momentz.tz(tz);

	return {
		preset,
		custom: {
			from: now.clone().subtract(rollingPreset.amount, rollingPreset.unit).toISOString(),
			to: now.toISOString(),
			tz,
		},
	};
}

function cloneArray(value){
	return Array.isArray(value) ? value.slice() : [];
}

function getMetadataDefaults(metadata){
	return metadata && metadata.defaults ? metadata.defaults : {};
}

function getDefaultTable(metadata, defaults){
	if (defaults.table) return defaults.table;
	if (metadata && Array.isArray(metadata.tables) && metadata.tables.length > 0) {
		return metadata.tables[0].key || metadata.tables[0].id || null;
	}
	if (metadata && Array.isArray(metadata.datasources) && metadata.datasources.length > 0) {
		return metadata.datasources[0].table || metadata.datasources[0].defaultTable || null;
	}
	return null;
}

function normalizeChartType(value){
	const chartType = value || DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE;
	return DASHBOARD_PIE_EVENTS_SUPPORTED_CHART_TYPES.includes(chartType)
		? chartType
		: DASHBOARD_PIE_EVENTS_DEFAULT_CHART_TYPE;
}

function normalizeKpiAppearance(value){
	const source = value || {};
	return {
		color: source.color || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.color,
		icon: source.icon || DASHBOARD_PIE_EVENTS_DEFAULT_KPI_APPEARANCE.icon,
	};
}

export function getDefaultPieEventsConfig(metadata){
	const defaults = getMetadataDefaults(metadata);

	return {
		schemaVersion: DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
		name: defaults.name || defaults.title || '',
		chartType: normalizeChartType(defaults.chartType || defaults.chart_type),
		metric: defaults.metric || null,
		datasource: defaults.datasource || null,
		table: getDefaultTable(metadata, defaults),
		devices: [],
		time: {
			preset: defaults.timePreset || defaults.preset || DEFAULT_TIME_PRESET,
			custom: {
				from: null,
				to: null,
				tz: defaults.tz || DEFAULT_TIMEZONE,
			},
		},
		groupBy: defaults.groupBy || defaults.group_by || defaults.defaultGroupBy || null,
		filters: [],
		limit: defaults.limit || DASHBOARD_PIE_EVENTS_DEFAULT_LIMIT,
		refreshSeconds: defaults.refreshSeconds || DASHBOARD_PIE_EVENTS_DEFAULT_REFRESH_SECONDS,
		appearance: normalizeKpiAppearance(defaults.appearance),
	};
}

export function normalizePieEventsConfig(config, metadata){
	const defaults = getDefaultPieEventsConfig(metadata);
	const source = config || {};
	const time = source.time || source.interval || defaults.time;
	const name = source.name || source.title || defaults.name || '';
	const titleKey = source.titleKey || source.title_key || defaults.titleKey || defaults.title_key || null;

	return Object.assign({}, defaults, source, {
		schemaVersion: DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
		name,
		title: source.title || name,
		titleKey,
		chartType: normalizeChartType(source.chartType || source.chart_type || defaults.chartType),
		devices: cloneArray(source.devices),
		time: normalizeTimeRange(time, defaults),
		filters: cloneArray(source.filters),
		limit: source.limit || defaults.limit,
		refreshSeconds: source.refreshSeconds || defaults.refreshSeconds,
		groupBy: source.groupBy || source.group_by || defaults.groupBy,
		appearance: normalizeKpiAppearance(source.appearance || defaults.appearance),
	});
}

export function buildPieEventsRequestPayload(config){
	const normalized = normalizePieEventsConfig(config);
	const payload = {
		chartType: normalized.chartType,
		datasource: normalized.datasource,
		table: normalized.table,
		devices: normalized.devices,
		time: normalized.time,
		filters: normalized.filters,
		limit: normalized.limit,
	};

	if (normalized.metric) {
		payload.metric = normalized.metric;
	}

	if (normalized.time && normalized.time.custom) {
		payload.from = normalized.time.custom.from;
		payload.to = normalized.time.custom.to;
	}

	if (normalized.groupBy) {
		payload.group_by = Array.isArray(normalized.groupBy)
			? normalized.groupBy.slice()
			: [normalized.groupBy];
	}

	return payload;
}

export function buildPieEventsPreviewPayload(config){
	const query = buildPieEventsRequestPayload(config);

	return Object.assign({}, query, {
		schemaVersion: DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
		kind: DASHBOARD_PIE_EVENTS_KIND,
		config: query,
	});
}

export function buildPieEventsStreamData(config, preview){
	const normalized = normalizePieEventsConfig(config);
	if (normalized.chartType !== 'kpi') delete normalized.appearance;
	const streamData = {
		schemaVersion: DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
		kind: DASHBOARD_PIE_EVENTS_KIND,
		title: normalized.name || normalized.title || 'Dashboard chart',
		titleKey: normalized.titleKey || null,
		chartType: normalized.chartType,
		config: normalized,
	};

	if (preview) {
		streamData.preview = preview;
	}

	return streamData;
}

export function buildPieEventsDataPayload(streamData){
	const source = streamData && streamData.config ? streamData.config : streamData;
	const payload = {
		schemaVersion: DASHBOARD_PIE_EVENTS_SCHEMA_VERSION,
		kind: DASHBOARD_PIE_EVENTS_KIND,
		config: Object.assign(buildPieEventsRequestPayload(source), {
			refreshSeconds: normalizePieEventsConfig(source).refreshSeconds,
		}),
	};

	return payload;
}
